import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import {
  Trophy,
  Users,
  Gift,
  Share2,
  Star,
  Crown,
  Check,
  X,
  ChevronRight,
  Sparkles,
  TrendingUp,
  Heart,
  Zap,
  Award,
  Target,
  ArrowRight,
  Play
} from "lucide-react";
import ReferralOnboarding from "@/components/ReferralOnboarding";
import AppFooter from "@/components/AppFooter";

const ReferralProgramPage = () => {
  const [activeTab, setActiveTab] = useState("how-it-works");
  const [showTutorial, setShowTutorial] = useState(false);

  // Tier data with rewards
  const tiers = [
    {
      name: "Starter",
      icon: "👑",
      referrals: 0,
      color: "from-stone-400 to-stone-500",
      bgColor: "bg-stone-500/10",
      borderColor: "border-stone-500/30",
      rewards: [
        "Access to referral program",
        "Unique shareable link",
        "100 points per referral"
      ]
    },
    {
      name: "Supporter",
      icon: "🌟",
      referrals: 5,
      color: "from-blue-400 to-cyan-500",
      bgColor: "bg-blue-500/10",
      borderColor: "border-blue-500/30",
      rewards: [
        "Exclusive Supporter badge",
        "Early access to new features",
        "150 points per referral",
        "Priority in search results"
      ]
    },
    {
      name: "Advocate",
      icon: "⭐",
      referrals: 10,
      color: "from-purple-400 to-pink-500",
      bgColor: "bg-purple-500/10",
      borderColor: "border-purple-500/30",
      rewards: [
        "Advocate badge on profile",
        "500 bonus loyalty points",
        "Priority customer support",
        "200 points per referral"
      ]
    },
    {
      name: "Champion",
      icon: "🥇",
      referrals: 25,
      color: "from-yellow-400 to-amber-500",
      bgColor: "bg-yellow-500/10",
      borderColor: "border-yellow-500/30",
      rewards: [
        "Champion badge + profile highlight",
        "1 FREE month Pro membership",
        "Featured on leaderboard",
        "250 points per referral"
      ]
    },
    {
      name: "Ambassador",
      icon: "🏆",
      referrals: 50,
      color: "from-amber-400 to-yellow-500",
      bgColor: "bg-amber-500/10",
      borderColor: "border-amber-500/30",
      rewards: [
        "Exclusive Ambassador status",
        "1 month FREE Pro (Beta Promo)",
        "VIP support channel",
        "Co-marketing opportunities",
        "300 points per referral"
      ],
      featured: true
    }
  ];

  // Competitor comparison data
  const competitors = [
    {
      name: "MyHairChair",
      logo: "💈",
      highlight: true,
      features: {
        referralProgram: true,
        referralRewards: "100+ pts/referral + badges",
        freeTier: "30-day trial",
        commissionFee: "0% platform fee",
        targetAudience: "All beauty professionals",
        ambassadorProgram: true,
        loyaltyIntegration: true,
        socialSharing: true,
        mobileContacts: true,
        promoLandingPages: true
      }
    },
    {
      name: "Booksy",
      logo: "📘",
      features: {
        referralProgram: true,
        referralRewards: "Credit only",
        freeTier: false,
        commissionFee: "2.49% + $0.25",
        targetAudience: "General",
        ambassadorProgram: false,
        loyaltyIntegration: false,
        socialSharing: true,
        mobileContacts: false,
        promoLandingPages: false
      }
    },
    {
      name: "StyleSeat",
      logo: "💇",
      features: {
        referralProgram: true,
        referralRewards: "$5-10 credits",
        freeTier: true,
        commissionFee: "Up to 25%",
        targetAudience: "General",
        ambassadorProgram: false,
        loyaltyIntegration: false,
        socialSharing: true,
        mobileContacts: false,
        promoLandingPages: false
      }
    },
    {
      name: "Vagaro",
      logo: "💆",
      features: {
        referralProgram: false,
        referralRewards: "None",
        freeTier: false,
        commissionFee: "$25-85/mo",
        targetAudience: "General",
        ambassadorProgram: false,
        loyaltyIntegration: true,
        socialSharing: false,
        mobileContacts: false,
        promoLandingPages: false
      }
    },
    {
      name: "Square",
      logo: "⬛",
      features: {
        referralProgram: false,
        referralRewards: "None",
        freeTier: true,
        commissionFee: "2.6% + $0.10",
        targetAudience: "General",
        ambassadorProgram: false,
        loyaltyIntegration: true,
        socialSharing: false,
        mobileContacts: false,
        promoLandingPages: false
      }
    }
  ];

  const comparisonFeatures = [
    { key: "referralProgram", label: "Referral Program", icon: Users },
    { key: "referralRewards", label: "Referral Rewards", icon: Gift },
    { key: "ambassadorProgram", label: "Ambassador Program", icon: Trophy },
    { key: "loyaltyIntegration", label: "Loyalty Points Integration", icon: Star },
    { key: "socialSharing", label: "Social Media Sharing", icon: Share2 },
    { key: "mobileContacts", label: "Mobile Contact Picker", icon: Users },
    { key: "promoLandingPages", label: "Promo Landing Pages", icon: Target },
    { key: "freeTier", label: "Free Tier Available", icon: Zap },
    { key: "targetAudience", label: "Target Audience", icon: Heart },
    { key: "commissionFee", label: "Commission/Fees", icon: TrendingUp }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-950 via-stone-900 to-stone-950">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-stone-950/90 backdrop-blur-md border-b border-stone-800">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <img 
              src="/images/myhairchair-logo.png" 
              alt="MyHairChair" 
              className="h-10 sm:h-12 md:h-14 w-auto"
            />
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/login">
              <Button variant="ghost" className="text-stone-300 hover:text-white">
                Sign In
              </Button>
            </Link>
            <Link to="/register">
              <Button className="bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-black font-bold">
                Join Free
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-amber-500/10 via-transparent to-purple-500/10"></div>
        <div className="container mx-auto px-4 text-center relative z-10">
          <Badge className="bg-amber-500/20 text-amber-400 border border-amber-500/30 mb-6 px-4 py-1">
            <Trophy className="w-4 h-4 mr-2 inline" />
            Referral Program
          </Badge>
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
            Grow Together,{" "}
            <span className="bg-gradient-to-r from-amber-400 to-yellow-500 bg-clip-text text-transparent">
              Earn Together
            </span>
          </h1>
          <p className="text-xl text-stone-400 max-w-2xl mx-auto mb-8">
            Invite friends to MyHairChair and earn rewards for every successful referral. 
            Rise through the ranks from Starter to Ambassador and unlock exclusive perks.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register">
              <Button size="lg" className="bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-black font-bold px-8">
                Start Earning Now
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Button 
              size="lg" 
              variant="outline" 
              className="border-purple-500/50 text-purple-400 hover:bg-purple-500/10"
              onClick={() => setShowTutorial(true)}
            >
              <Play className="w-5 h-5 mr-2" />
              Watch Tutorial
            </Button>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-3 gap-6 max-w-lg mx-auto mt-12">
            <div className="text-center">
              <p className="text-3xl font-bold text-amber-400">100+</p>
              <p className="text-stone-500 text-sm">Points per Referral</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-green-400">5</p>
              <p className="text-stone-500 text-sm">Reward Tiers</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-purple-400">FREE</p>
              <p className="text-stone-500 text-sm">Pro Month at 50</p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-20 bg-stone-900/50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">How It Works</h2>
            <p className="text-stone-400 max-w-xl mx-auto">
              Three simple steps to start earning rewards and growing our community
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {/* Step 1 */}
            <div className="relative">
              <div className="absolute -top-4 -left-4 w-12 h-12 bg-gradient-to-br from-amber-500 to-yellow-500 rounded-full flex items-center justify-center text-black font-bold text-xl">
                1
              </div>
              <Card className="bg-stone-800/50 border-stone-700 h-full pt-8">
                <CardContent className="text-center p-6">
                  <div className="w-16 h-16 bg-amber-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Share2 className="w-8 h-8 text-amber-400" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">Get Your Unique Link</h3>
                  <p className="text-stone-400">
                    Sign up and receive your personal referral link. Share it on social media, 
                    via text, or directly from your contacts.
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Step 2 */}
            <div className="relative">
              <div className="absolute -top-4 -left-4 w-12 h-12 bg-gradient-to-br from-amber-500 to-yellow-500 rounded-full flex items-center justify-center text-black font-bold text-xl">
                2
              </div>
              <Card className="bg-stone-800/50 border-stone-700 h-full pt-8">
                <CardContent className="text-center p-6">
                  <div className="w-16 h-16 bg-green-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Users className="w-8 h-8 text-green-400" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">Friends Sign Up</h3>
                  <p className="text-stone-400">
                    When someone uses your link to register, they become your referral. 
                    Works for both clients and stylists!
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Step 3 */}
            <div className="relative">
              <div className="absolute -top-4 -left-4 w-12 h-12 bg-gradient-to-br from-amber-500 to-yellow-500 rounded-full flex items-center justify-center text-black font-bold text-xl">
                3
              </div>
              <Card className="bg-stone-800/50 border-stone-700 h-full pt-8">
                <CardContent className="text-center p-6">
                  <div className="w-16 h-16 bg-purple-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Gift className="w-8 h-8 text-purple-400" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">Earn Rewards</h3>
                  <p className="text-stone-400">
                    Instantly earn loyalty points, climb the ranks, and unlock exclusive 
                    perks like free Pro membership!
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Not a Coupon Code Note */}
          <div className="mt-12 max-w-2xl mx-auto">
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-6 text-center">
              <Sparkles className="w-8 h-8 text-blue-400 mx-auto mb-3" />
              <h4 className="text-white font-bold mb-2">It&apos;s More Than a Coupon Code!</h4>
              <p className="text-stone-400 text-sm">
                Unlike traditional coupon codes, our referral system tracks your unique link and 
                automatically credits you when friends sign up. No codes to remember, no manual entry—just 
                share and earn!
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Tier Levels Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Referral Tiers & Rewards</h2>
            <p className="text-stone-400 max-w-xl mx-auto">
              The more you refer, the more you earn. Climb the ranks and unlock exclusive benefits!
            </p>
          </div>

          <div className="grid md:grid-cols-5 gap-4 max-w-6xl mx-auto">
            {tiers.map((tier, idx) => (
              <Card 
                key={tier.name}
                className={`${tier.bgColor} ${tier.borderColor} border-2 relative overflow-hidden ${
                  tier.featured ? 'ring-2 ring-amber-500 shadow-lg shadow-amber-500/20' : ''
                }`}
              >
                {tier.featured && (
                  <div className="absolute top-0 right-0 bg-gradient-to-l from-amber-500 to-yellow-500 text-black text-xs font-bold px-3 py-1 rounded-bl-lg">
                    BETA PROMO
                  </div>
                )}
                <CardContent className="p-4 text-center">
                  <div className="text-4xl mb-2">{tier.icon}</div>
                  <h3 className="text-lg font-bold text-white mb-1">{tier.name}</h3>
                  <p className="text-amber-400 text-sm font-medium mb-4">
                    {tier.referrals === 0 ? "Start here" : `${tier.referrals} referrals`}
                  </p>
                  <ul className="space-y-2 text-left">
                    {tier.rewards.map((reward, i) => (
                      <li key={i} className="flex items-start gap-2 text-xs text-stone-300">
                        <Check className="w-3 h-3 text-green-400 mt-0.5 flex-shrink-0" />
                        <span>{reward}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Tier Progression Visual */}
          <div className="mt-12 max-w-4xl mx-auto">
            <div className="flex items-center justify-between relative">
              <div className="absolute top-1/2 left-0 right-0 h-1 bg-stone-700 -translate-y-1/2"></div>
              {tiers.map((tier, idx) => (
                <div key={tier.name} className="relative z-10 text-center">
                  <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${tier.color} flex items-center justify-center text-2xl shadow-lg`}>
                    {tier.icon}
                  </div>
                  <p className="text-stone-400 text-xs mt-2">{tier.referrals}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Competitor Comparison Section */}
      <section className="py-20 bg-stone-900/50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <Badge className="bg-purple-500/20 text-purple-400 border border-purple-500/30 mb-4">
              <Award className="w-4 h-4 mr-2 inline" />
              Why Choose Us
            </Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              How We Compare to Competitors
            </h2>
            <p className="text-stone-400 max-w-xl mx-auto">
              See why MyHairChair&apos;s referral program stands out from the competition
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full max-w-5xl mx-auto text-left">
              <thead>
                <tr className="border-b border-stone-700">
                  <th className="py-4 px-4 text-stone-400 font-medium">Feature</th>
                  {competitors.map((comp) => (
                    <th 
                      key={comp.name} 
                      className={`py-4 px-4 text-center ${
                        comp.highlight ? 'bg-amber-500/10' : ''
                      }`}
                    >
                      <div className="flex flex-col items-center gap-1">
                        <span className="text-2xl">{comp.logo}</span>
                        <span className={`font-bold ${comp.highlight ? 'text-amber-400' : 'text-white'}`}>
                          {comp.name}
                        </span>
                        {comp.highlight && (
                          <Badge className="bg-amber-500 text-black text-xs">YOU ARE HERE</Badge>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {comparisonFeatures.map((feature, idx) => (
                  <tr 
                    key={feature.key}
                    className={`border-b border-stone-800 ${idx % 2 === 0 ? 'bg-stone-800/20' : ''}`}
                  >
                    <td className="py-4 px-4">
                      <div className="flex items-center gap-2 text-stone-300">
                        <feature.icon className="w-4 h-4 text-stone-500" />
                        {feature.label}
                      </div>
                    </td>
                    {competitors.map((comp) => {
                      const value = comp.features[feature.key];
                      return (
                        <td 
                          key={comp.name} 
                          className={`py-4 px-4 text-center ${
                            comp.highlight ? 'bg-amber-500/10' : ''
                          }`}
                        >
                          {typeof value === 'boolean' ? (
                            value ? (
                              <Check className={`w-5 h-5 mx-auto ${comp.highlight ? 'text-green-400' : 'text-green-500'}`} />
                            ) : (
                              <X className="w-5 h-5 mx-auto text-red-500/50" />
                            )
                          ) : (
                            <span className={`text-sm ${
                              comp.highlight ? 'text-amber-400 font-medium' : 'text-stone-400'
                            }`}>
                              {value}
                            </span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Key Differentiators */}
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto mt-12">
            <Card className="bg-stone-800/50 border-stone-700">
              <CardContent className="p-6 text-center">
                <Heart className="w-10 h-10 text-pink-400 mx-auto mb-3" />
                <h4 className="text-white font-bold mb-2">Built for Our Community</h4>
                <p className="text-stone-400 text-sm">
                  Designed for beauty professionals with culturally-aware features.
                </p>
              </CardContent>
            </Card>
            <Card className="bg-stone-800/50 border-stone-700">
              <CardContent className="p-6 text-center">
                <Zap className="w-10 h-10 text-yellow-400 mx-auto mb-3" />
                <h4 className="text-white font-bold mb-2">30-Day Free Trial</h4>
                <p className="text-stone-400 text-sm">
                  Try all Pro features free for 30 days. No credit card required to start.
                </p>
              </CardContent>
            </Card>
            <Card className="bg-stone-800/50 border-stone-700">
              <CardContent className="p-6 text-center">
                <Trophy className="w-10 h-10 text-amber-400 mx-auto mb-3" />
                <h4 className="text-white font-bold mb-2">Real Rewards, Real Value</h4>
                <p className="text-stone-400 text-sm">
                  Earn points that convert to real benefits—not just credits that expire.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="bg-gradient-to-r from-amber-500/20 via-yellow-500/20 to-amber-500/20 border border-amber-500/30 rounded-3xl p-12 text-center max-w-4xl mx-auto">
            <Trophy className="w-16 h-16 text-amber-400 mx-auto mb-6" />
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Ready to Start Earning?
            </h2>
            <p className="text-stone-400 mb-8 max-w-lg mx-auto">
              Join thousands of beauty professionals already growing their business 
              and earning rewards with MyHairChair.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/register?role=stylist">
                <Button size="lg" className="bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-black font-bold px-8">
                  Join as Stylist/Barber
                  <ChevronRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to="/register?role=client">
                <Button size="lg" variant="outline" className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10 px-8">
                  Join as Client
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Full Footer */}
      <AppFooter />

      {/* Tutorial Modal */}
      <Dialog open={showTutorial} onOpenChange={setShowTutorial}>
        <DialogContent className="max-w-2xl bg-stone-900 border-stone-700 p-0 overflow-hidden" aria-describedby="tutorial-desc">
          <p id="tutorial-desc" className="sr-only">Interactive tutorial on how the referral program works</p>
          <ReferralOnboarding 
            onComplete={() => setShowTutorial(false)} 
            autoPlay={true}
            showCloseButton={true}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ReferralProgramPage;
